import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, FileText, Users, Plus, X, Mail } from 'lucide-react';
import { toast } from 'sonner';
import {
  requestContractDraft,
  type CreateContractDraftPayload,
  type AdhesionContato,
} from '@/services/adhesionService';
import type { ResponsavelData } from '@/services/conexaService';

// =============================================================================
// Etapa 5 — Contrato Formal (opcional)
// =============================================================================
// Aparece apenas quando o cliente marcou "Quero também receber minuta de
// contrato formal" no aceite da Etapa 4. Coleta:
//   - Representante(s) Legal(is): escolhidos entre sócios do CNPJ ou manuais
//   - Testemunha pela CONTRATANTE (obrigatória — CPC art. 784, III)
//   - Emails adicionais (jurídico, etc) — max 5
//
// Template autoritativo: dgenny-contratos/contrato-completo/contrato-completo-dgenny.md
// Dicionário de placeholders: dgenny-contratos/contrato-completo/placeholders.md
//
// Política de testemunhas (do placeholders.md):
//   - Testemunha 1: da CONTRATANTE → coletada neste form
//   - Testemunha 2: da DGENNY → preenchida pelo backend (colaborador padrão)
//
// Chama `requestContractDraft` no backend, que gera minuta e envia por email
// pra todos os destinatários (legais + testemunha 1 + operacional + financeiro
// + emails adicionais), vinculados ao mesmo `company.id` (grupo econômico).
// O vendedor responsável recebe cópia oculta (BCC) e o Slack de onboarding é
// notificado pelo backend.
// =============================================================================

const MAX_EMAILS_ADICIONAIS = 5;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ContratoFormalEtapaProps {
  adhesionId: string;
  companyId: string;
  /** Sócios pré-carregados do CNPJ (lookup da Etapa 1). */
  socios: ResponsavelData[];
  /** Operacional já cadastrado no Radar via aceite. Usado no resumo de destinatários. */
  responsavelOperacional: { nome: string; email: string };
  /** Financeiro já cadastrado no Radar via aceite. Usado no resumo de destinatários. */
  responsavelFinanceiro: { nome: string; email: string };
  onBack: () => void;
  onCompleted: () => void;
}

interface LegalEntry {
  /** Identifica o sócio do CNPJ (se aplicável) ou 'manual_<idx>' pra entries manuais. */
  origin: string;
  nome: string;
  cpf: string;
  email: string;
  celular: string;
  cargo: string;
  /** Sócios podem vir com CPF mascarado (***); nesse caso força preenchimento manual. */
  cpfMascarado: boolean;
}

function gerarIdempotencyKey(): string {
  // UUID v4 (best-effort): usa crypto.randomUUID se disponível, fallback timestamp.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idk_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export function ContratoFormalEtapa({
  adhesionId,
  companyId,
  socios,
  responsavelOperacional,
  responsavelFinanceiro,
  onBack,
  onCompleted,
}: ContratoFormalEtapaProps) {
  const [legais, setLegais] = useState<LegalEntry[]>([]);
  // Testemunha 1 da CONTRATANTE — obrigatória pelo formato contrato (CPC
  // art. 784, III). Testemunha 2 é da DGENNY e é preenchida pelo backend.
  const [testemunha, setTestemunha] = useState({ nome: '', cpf: '', email: '' });
  const [emailsAdicionais, setEmailsAdicionais] = useState<string[]>(['']);
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyKey] = useState(() => gerarIdempotencyKey());

  // Toggle sócio como legal: adiciona ou remove o LegalEntry derivado dele.
  function toggleSocioLegal(socio: ResponsavelData) {
    setLegais((prev) => {
      const existe = prev.find((l) => l.origin === `socio_${socio.nome}`);
      if (existe) {
        return prev.filter((l) => l.origin !== `socio_${socio.nome}`);
      }
      const cpfMascarado = socio.cpf.includes('*');
      return [
        ...prev,
        {
          origin: `socio_${socio.nome}`,
          nome: socio.nome,
          cpf: cpfMascarado ? '' : socio.cpf,
          email: socio.email || '',
          celular: socio.celular || '',
          cargo: socio.cargo || 'Representante Legal',
          cpfMascarado,
        },
      ];
    });
  }

  function adicionarLegalManual() {
    setLegais((prev) => [
      ...prev,
      {
        origin: `manual_${prev.length}_${Date.now()}`,
        nome: '',
        cpf: '',
        email: '',
        celular: '',
        cargo: 'Representante Legal',
        cpfMascarado: false,
      },
    ]);
  }

  function removerLegal(origin: string) {
    setLegais((prev) => prev.filter((l) => l.origin !== origin));
  }

  function atualizarLegal(origin: string, patch: Partial<LegalEntry>) {
    setLegais((prev) => prev.map((l) => (l.origin === origin ? { ...l, ...patch } : l)));
  }

  function adicionarEmailAdicional() {
    if (emailsAdicionais.length >= MAX_EMAILS_ADICIONAIS) return;
    setEmailsAdicionais((prev) => [...prev, '']);
  }

  function removerEmailAdicional(idx: number) {
    setEmailsAdicionais((prev) => prev.filter((_, i) => i !== idx));
  }

  function atualizarEmailAdicional(idx: number, valor: string) {
    setEmailsAdicionais((prev) => prev.map((e, i) => (i === idx ? valor : e)));
  }

  // Resumo de destinatários únicos. Operacional e financeiro vão de "brinde"
  // (já cadastrados no Radar via aceite). Dedupe por email (case-insensitive).
  const destinatariosResumo = useMemo(() => {
    const emails = new Set<string>();
    const add = (email: string) => {
      const e = email.trim().toLowerCase();
      if (e && EMAIL_REGEX.test(e)) emails.add(e);
    };
    if (responsavelOperacional.email) add(responsavelOperacional.email);
    if (responsavelFinanceiro.email) add(responsavelFinanceiro.email);
    legais.forEach((l) => add(l.email));
    if (testemunha.email) add(testemunha.email);
    emailsAdicionais.forEach(add);
    return {
      total: emails.size,
      legais: legais.filter((l) => l.email).length,
      temTestemunha: !!testemunha.nome,
      emailsExtras: emailsAdicionais.filter((e) => e && EMAIL_REGEX.test(e)).length,
    };
  }, [legais, testemunha, emailsAdicionais, responsavelOperacional, responsavelFinanceiro]);

  function validar(): string | null {
    if (legais.length === 0) {
      return 'Selecione pelo menos um representante legal (sócio ou manual).';
    }
    for (const l of legais) {
      if (!l.nome.trim()) return `Preencha o nome do representante legal "${l.origin}".`;
      if (!l.email.trim() || !EMAIL_REGEX.test(l.email)) {
        return `E-mail inválido para o representante legal ${l.nome}.`;
      }
      if (l.cpfMascarado && !l.cpf.trim()) {
        return `CPF do representante ${l.nome} veio mascarado pelo CNPJ — preencha manualmente.`;
      }
    }
    if (!testemunha.nome.trim() || !testemunha.cpf.trim()) {
      return 'Preencha nome e CPF da testemunha pela contratante (obrigatório no formato contrato).';
    }
    if (testemunha.email && !EMAIL_REGEX.test(testemunha.email)) {
      return 'E-mail da testemunha é inválido.';
    }
    const emailsValidos = emailsAdicionais.filter((e) => e.trim());
    for (const e of emailsValidos) {
      if (!EMAIL_REGEX.test(e)) return `E-mail adicional "${e}" é inválido.`;
    }
    return null;
  }

  async function handleEnviar() {
    const erro = validar();
    if (erro) {
      toast.error(erro);
      return;
    }

    const representantesLegais: AdhesionContato[] = legais.map((l) => ({
      nome: l.nome.trim(),
      cargo: l.cargo || 'Representante Legal',
      cpf: l.cpf.trim() || undefined,
      email: l.email.trim(),
      telefone: l.celular.trim(),
    }));

    const emailsValidos = Array.from(
      new Set(
        emailsAdicionais
          .map((e) => e.trim().toLowerCase())
          .filter((e) => e && EMAIL_REGEX.test(e)),
      ),
    );

    const payload: CreateContractDraftPayload = {
      adhesionId,
      representantes_legais: representantesLegais,
      testemunha: {
        nome: testemunha.nome.trim(),
        cpf: testemunha.cpf.trim(),
        ...(testemunha.email.trim() && { email: testemunha.email.trim() }),
      },
      emails_adicionais: emailsValidos,
      idempotency_key: idempotencyKey,
    };

    setSubmitting(true);
    try {
      const result = await requestContractDraft(payload);
      if (result.emailsEnviados > 0) {
        toast.success(`Minuta enviada para ${result.emailsEnviados} destinatário(s).`);
      } else {
        // Draft gerado mas o envio não foi enfileirado (falha rara no backend,
        // já alertada no Slack) — o time despacha manualmente.
        toast.success('Pedido de minuta registrado. Você receberá em até 1 dia útil.');
      }
      onCompleted();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao gerar minuta. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-brand-azul mb-2">Contrato Formal — Minuta por e-mail</h2>
        <p className="text-sm text-muted-foreground">
          Indique quem assinará o contrato formal pela contratante. A minuta será gerada
          a partir do termo aceito e enviada por e-mail aos destinatários.
        </p>
        {!companyId && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Aviso: companyId não disponível — a minuta poderá ser vinculada manualmente no Radar.
          </p>
        )}
      </div>

      {/* Bloco 1 — Representantes Legais */}
      <Card className="p-4">
        <h3 className="font-semibold text-brand-azul mb-1 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Representantes legais (assinarão)
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Marque os sócios que assinarão e complete os dados de contato. Para representantes
          que não aparecem na lista, use "Adicionar manualmente".
        </p>

        {socios.length === 0 && (
          <p className="text-sm text-muted-foreground italic mb-3">
            Nenhum sócio identificado pelo CNPJ. Adicione manualmente abaixo.
          </p>
        )}

        {socios.map((socio, idx) => {
          const entry = legais.find((l) => l.origin === `socio_${socio.nome}`);
          const selecionado = !!entry;
          return (
            <div
              key={`socio-${idx}`}
              className={`border rounded-md p-3 mb-2 ${
                selecionado ? 'border-brand-roxo bg-brand-roxo/5' : 'border-border'
              }`}
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selecionado}
                  onChange={() => toggleSocioLegal(socio)}
                  className="w-4 h-4"
                />
                <span className="font-medium text-sm">{socio.nome}</span>
                <span className="text-xs text-muted-foreground">· {socio.cargo}</span>
              </label>
              {selecionado && entry && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 pl-6">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      CPF {entry.cpfMascarado && <span className="text-red-600">*</span>}
                    </Label>
                    <Input
                      value={entry.cpf}
                      onChange={(e) => atualizarLegal(entry.origin, { cpf: e.target.value })}
                      placeholder={entry.cpfMascarado ? 'Preencha (veio mascarado)' : '000.000.000-00'}
                      className="text-sm h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">E-mail *</Label>
                    <Input
                      type="email"
                      value={entry.email}
                      onChange={(e) => atualizarLegal(entry.origin, { email: e.target.value })}
                      placeholder="legal@empresa.com"
                      className="text-sm h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Celular</Label>
                    <Input
                      value={entry.celular}
                      onChange={(e) => atualizarLegal(entry.origin, { celular: e.target.value })}
                      placeholder="(00) 00000-0000"
                      className="text-sm h-8"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {legais
          .filter((l) => l.origin.startsWith('manual_'))
          .map((entry) => (
            <div
              key={entry.origin}
              className="border border-brand-roxo bg-brand-roxo/5 rounded-md p-3 mb-2"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Representante manual</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removerLegal(entry.origin)}
                  className="h-6 px-2 text-red-600 hover:text-red-700"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Nome completo *</Label>
                  <Input
                    value={entry.nome}
                    onChange={(e) => atualizarLegal(entry.origin, { nome: e.target.value })}
                    placeholder="Nome do representante"
                    className="text-sm h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cargo</Label>
                  <Input
                    value={entry.cargo}
                    onChange={(e) => atualizarLegal(entry.origin, { cargo: e.target.value })}
                    placeholder="Representante Legal"
                    className="text-sm h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">CPF</Label>
                  <Input
                    value={entry.cpf}
                    onChange={(e) => atualizarLegal(entry.origin, { cpf: e.target.value })}
                    placeholder="000.000.000-00"
                    className="text-sm h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">E-mail *</Label>
                  <Input
                    type="email"
                    value={entry.email}
                    onChange={(e) => atualizarLegal(entry.origin, { email: e.target.value })}
                    placeholder="legal@empresa.com"
                    className="text-sm h-8"
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Celular</Label>
                  <Input
                    value={entry.celular}
                    onChange={(e) => atualizarLegal(entry.origin, { celular: e.target.value })}
                    placeholder="(00) 00000-0000"
                    className="text-sm h-8"
                  />
                </div>
              </div>
            </div>
          ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={adicionarLegalManual}
          className="mt-2"
        >
          <Plus className="w-3 h-3 mr-1" />
          Adicionar representante manual
        </Button>
      </Card>

      {/* Bloco 2 — Testemunha 1 (pela CONTRATANTE) — obrigatória */}
      <Card className="p-4">
        <h3 className="font-semibold text-brand-azul flex items-center gap-2 mb-1">
          <FileText className="w-4 h-4" />
          Testemunha pela contratante
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Obrigatória pelo formato contrato (CPC art. 784, III). A segunda
          testemunha é da DGenny e é preenchida automaticamente.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Nome *</Label>
            <Input
              value={testemunha.nome}
              onChange={(e) => setTestemunha((p) => ({ ...p, nome: e.target.value }))}
              placeholder="Nome completo"
              className="text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">CPF *</Label>
            <Input
              value={testemunha.cpf}
              onChange={(e) => setTestemunha((p) => ({ ...p, cpf: e.target.value }))}
              placeholder="000.000.000-00"
              className="text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">E-mail</Label>
            <Input
              type="email"
              value={testemunha.email}
              onChange={(e) => setTestemunha((p) => ({ ...p, email: e.target.value }))}
              placeholder="testemunha@empresa.com (opcional)"
              className="text-sm"
            />
          </div>
        </div>
      </Card>

      {/* Bloco 3 — Emails adicionais */}
      <Card className="p-4">
        <h3 className="font-semibold text-brand-azul mb-1 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          E-mails adicionais (jurídico, etc)
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Até {MAX_EMAILS_ADICIONAIS} e-mails extras que devem receber cópia da minuta.
          Duplicatas com contatos já cadastrados são removidas automaticamente.
        </p>
        {emailsAdicionais.map((email, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => atualizarEmailAdicional(idx, e.target.value)}
              placeholder="juridico@empresa.com"
              className="text-sm flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removerEmailAdicional(idx)}
              className="h-9 px-2 text-red-600 hover:text-red-700"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ))}
        {emailsAdicionais.length < MAX_EMAILS_ADICIONAIS && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={adicionarEmailAdicional}
            className="mt-1"
          >
            <Plus className="w-3 h-3 mr-1" />
            Adicionar e-mail
          </Button>
        )}
      </Card>

      {/* Resumo de destinatários */}
      <Card className="p-4 bg-brand-azul/5 border-brand-azul/20">
        <h3 className="font-semibold text-brand-azul mb-2 text-sm">A minuta será enviada para:</h3>
        <p className="text-sm">
          <strong>{destinatariosResumo.total}</strong> destinatário(s) único(s) —{' '}
          {destinatariosResumo.legais} representante(s) legal(is)
          {destinatariosResumo.temTestemunha && testemunha.email && ', 1 testemunha'}
          , operacional, financeiro
          {destinatariosResumo.emailsExtras > 0 &&
            `, ${destinatariosResumo.emailsExtras} e-mail(s) adicional(is)`}
          . Email da testemunha é opcional — sem ele, ela só consta na minuta.
        </p>
      </Card>

      <div className="flex justify-between gap-3">
        <Button variant="outline" onClick={onBack} disabled={submitting}>
          Voltar
        </Button>
        <Button
          onClick={handleEnviar}
          disabled={submitting || legais.length === 0}
          className="bg-brand-roxo hover:bg-brand-roxo/90"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enviando minuta...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Gerar e enviar minuta
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
